/* BEATSEEK — moderation queue for community ID corrections. */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ConfirmationsSummary = () => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/track-id/confirmations/top?limit=8`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('beatseek_token') || ''}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setRows(d?.confirmations || []))
      .catch(() => {});
  }, []);

  if (rows.length === 0) return null;

  return (
    <div className="rounded-lg border border-emerald-700/40 bg-emerald-900/10 p-4" data-testid="id-confirmations-summary">
      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-emerald-300">✓ Most-confirmed IDs</div>
      <div className="space-y-1">
        {rows.map(r => (
          <div key={r.key} className="flex items-center gap-2 text-xs text-gray-300">
            <span className="w-8 shrink-0 font-bold text-emerald-400">{r.count}×</span>
            <span className="truncate">{r.artist} – {r.title}</span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[10px] text-gray-500">Listener confirmations that the engine named these correctly — spikes here are worth a look.</p>
    </div>
  );
};

export const IdCorrectionsPanel = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);

  const load = async (s = status) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/track-id/corrections?status=${s}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('beatseek_token') || ''}` },
      });
      const d = await r.json().catch(() => ({}));
      setItems(r.ok ? (d.corrections || []) : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  const act = async (id, action) => {
    const r = await fetch(`${API}/api/track-id/corrections/${id}/${action}`, {
      method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('beatseek_token') || ''}` },
    });
    if (r.ok) {
      toast.success(action === 'approve' ? 'Approved — the engine will use this version from now on' : 'Correction rejected');
      load(status);
    } else {
      toast.error('Could not update that correction');
    }
  };

  return (
    <div className="space-y-4" data-testid="id-corrections-tab-content">
      <ConfirmationsSummary />
      <div className="flex items-center gap-2">
        {['pending', 'approved', 'rejected'].map(s => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`id-corrections-filter-${s}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold capitalize transition-colors ${status === s ? 'bg-pink-600/25 text-pink-200 border border-pink-500/50' : 'border border-white/10 text-gray-400 hover:text-white'}`}
          >
            {s}
          </button>
        ))}
        <span className="ml-auto text-xs text-gray-500">Approved corrections re-label future IDs of the same original</span>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-gray-400">Loading corrections…</div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500">No {status} corrections.</div>
      ) : (
        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {items.map((c, idx) => (
            <div key={c.id} data-testid={`id-correction-item-${idx}`} className="rounded-lg border border-pink-700/40 bg-pink-900/10 p-4">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full bg-pink-500/20 px-2 py-0.5 font-semibold uppercase tracking-wide text-pink-300">{c.suggested_version_type}</span>
                <span className="text-gray-400">from <strong className="text-gray-200">{c.username}</strong></span>
                <span className="text-gray-600">{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm text-gray-300">
                <div className="line-through decoration-red-500/60">{c.identified_artist} – {c.identified_title}</div>
                <div className="font-semibold text-white">
                  {c.suggested_artist || c.identified_artist} – {c.suggested_title || c.identified_title}
                </div>
              </div>
              {c.link && <a href={c.link} target="_blank" rel="noreferrer" className="mt-1 block truncate text-xs text-cyan-400 underline">{c.link}</a>}
              {c.notes && <p className="mt-1 text-xs italic text-gray-400">“{c.notes}”</p>}
              {status === 'pending' && (
                <div className="mt-3 flex gap-2">
                  <button onClick={() => act(c.id, 'approve')} data-testid={`id-correction-approve-${idx}`} className="rounded-full bg-green-600/25 px-4 py-1.5 text-xs font-bold text-green-300 transition-colors hover:bg-green-600/40">
                    ✓ Approve &amp; teach the engine
                  </button>
                  <button onClick={() => act(c.id, 'reject')} data-testid={`id-correction-reject-${idx}`} className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-gray-400 transition-colors hover:text-white">
                    Reject
                  </button>
                </div>
              )}
              {c.status === 'approved' && <div className="mt-2 text-xs font-semibold text-green-400">Approved · used {c.hits || 0}× by the engine</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IdCorrectionsPanel;
